import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { mutate } from 'swr';

import { Button } from 'src/components/Buttons/Button';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { Creator } from 'src/domain/creator';
import {
    Organization,
    OrganizationModule,
    OrganizationModuleType,
    OrganizationStructureVersion,
} from 'src/domain/organization';
import { getFullName } from 'src/domain/user';
import { formatPhoneNumber } from 'src/utils/stringUtils';
import { useAvatar } from 'src/view/hooks/useAvatar';

const {
    cradle: { munduClient },
} = container;

type ModuleMembersSettingsProps = {
    organizationId: string;
    currentUserId?: string;
    organization?: Organization;
    moduleType: OrganizationModuleType;
    module?: OrganizationModule;
    canManage: boolean;
    description: string;
};

type ModuleMemberCandidate = Pick<Creator, 'id' | 'profile'> & {
    email?: string;
    phoneNumber?: string;
    avatarUrl?: string;
};

type MemberRowProps = {
    employee?: ModuleMemberCandidate;
    fallbackId?: string;
    action?: {
        label: string;
        onClick: () => void;
        variant: 'add' | 'remove';
        disabled?: boolean;
    };
};

const getMemberContact = (employee: ModuleMemberCandidate) => {
    if (employee.email) return employee.email;
    if (!employee.phoneNumber) return undefined;

    return (
        formatPhoneNumber(employee.phoneNumber)?.trim() ?? employee.phoneNumber
    );
};

const MemberRow = ({ employee, fallbackId, action }: MemberRowProps) => {
    const { t } = useTranslation();
    const contact = employee ? getMemberContact(employee) : undefined;
    const name = employee
        ? getFullName(employee) || contact
        : t('modules.moduleMembers.formerEmployee');
    const avatar = useAvatar(employee?.profile.avatar, {
        firstName: employee?.profile.firstName,
        lastName: employee?.profile.lastName,
        displayName: name,
    });
    const src = employee?.avatarUrl ?? avatar.src;
    const isDefault = !employee?.avatarUrl && avatar.isDefault;

    return (
        <li className="flex min-w-0 items-center gap-3 px-4 py-3 transition-colors hover:bg-lightPeach hover:bg-opacity-30">
            {isDefault ? (
                <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lightPeach text-sm font-semibold uppercase text-woodyBrown"
                    aria-hidden="true"
                >
                    {avatar.initials || '?'}
                </span>
            ) : (
                <img
                    src={src}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                />
            )}
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                    {name}
                </span>
                <span className="block truncate text-xs text-woodyBrown text-opacity-50">
                    {employee
                        ? contact
                        : t(
                              'modules.moduleMembers.formerEmployeeDescriptionShort',
                              { id: fallbackId },
                          )}
                </span>
            </span>
            {action && (
                <button
                    type="button"
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className={
                        action.disabled
                            ? 'primary-btn-small shrink-0 px-3 py-1.5 text-sm'
                            : action.variant === 'add'
                            ? 'primary-btn-small shrink-0 px-3 py-1.5 text-sm'
                            : 'secondary-btn-small shrink-0 px-3 py-1.5 text-sm'
                    }
                >
                    {action.label}
                </button>
            )}
        </li>
    );
};

export const ModuleMembersSettings = ({
    organizationId,
    currentUserId,
    organization,
    moduleType,
    module,
    canManage,
    description,
}: ModuleMembersSettingsProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [draftMemberIds, setDraftMemberIds] = useState<string[] | null>(null);
    const [search, setSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const { data: employees } = useSWR(
        organizationId ? ['org-creators', organizationId] : null,
        (_, orgId: string) =>
            munduClient.listCreators({ organizationId: orgId }),
    );

    const savedMemberIds = useMemo(
        () => module?.memberIds ?? [],
        [module?.memberIds],
    );
    const memberIds = draftMemberIds ?? savedMemberIds;
    const isEditing = draftMemberIds !== null;
    const memberLimit = module?.memberLimit;
    const memberIdSet = useMemo(() => new Set(memberIds), [memberIds]);
    const isAtLimit =
        typeof memberLimit === 'number' && memberIds.length >= memberLimit;
    const canAddSelf =
        canManage &&
        currentUserId === organizationId &&
        !savedMemberIds.includes(organizationId);
    const memberCandidates = useMemo<ModuleMemberCandidate[]>(() => {
        const originalAccount =
            organization &&
            organization.structureVersion !==
                OrganizationStructureVersion.separated &&
            !organization.legacyAdministratorRetiredAt
                ? [
                      {
                          id: organization.id,
                          email: organization.email,
                          phoneNumber: organization.phoneNumber,
                          profile: {
                              firstName: organization.profile.firstName,
                              lastName: organization.profile.lastName,
                              avatar: organization.superuserAvatar,
                          },
                          avatarUrl: organization.superuserAvatarUrl,
                      },
                  ]
                : [];

        return [
            ...originalAccount,
            ...(employees ?? []).filter(
                employee => employee.id !== organization?.id,
            ),
        ];
    }, [employees, organization]);
    const employeeById = useMemo(
        () =>
            new Map(memberCandidates.map(employee => [employee.id, employee])),
        [memberCandidates],
    );
    const hasChanges =
        isEditing &&
        (memberIds.length !== savedMemberIds.length ||
            memberIds.some(memberId => !savedMemberIds.includes(memberId)));
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const availableEmployees = useMemo(
        () =>
            memberCandidates
                .filter(employee => !memberIdSet.has(employee.id))
                .filter(employee => {
                    if (!normalizedSearch) return true;

                    const formattedPhoneNumber = employee.phoneNumber
                        ? formatPhoneNumber(employee.phoneNumber)
                        : '';

                    return `${getFullName(employee)} ${employee.email ?? ''} ${
                        employee.phoneNumber ?? ''
                    } ${formattedPhoneNumber ?? ''}`
                        .toLocaleLowerCase()
                        .includes(normalizedSearch);
                })
                .slice(0, 8),
        [memberCandidates, memberIdSet, normalizedSearch],
    );

    const startEditing = () => {
        setDraftMemberIds([...savedMemberIds]);
        setSearch('');
    };

    const cancelEditing = () => {
        if (isSaving) return;
        setDraftMemberIds(null);
        setSearch('');
    };

    const addMember = (memberId: string) => {
        if (
            typeof memberLimit === 'number' &&
            memberIds.length >= memberLimit
        ) {
            notify(
                t('modules.moduleMembers.limitReached', {
                    limit: memberLimit,
                }),
                true,
            );
            return;
        }

        setDraftMemberIds(current => [
            ...(current ?? savedMemberIds),
            memberId,
        ]);
        setSearch('');
    };

    const removeMember = (memberId: string) => {
        setDraftMemberIds(current =>
            (current ?? savedMemberIds).filter(id => id !== memberId),
        );
    };

    const saveMembers = async () => {
        if (!hasChanges) return;

        setIsSaving(true);
        try {
            await munduClient.updateOrganizationModule(organizationId, {
                type: moduleType,
                memberIds,
            });
            await mutate(['organization', organizationId]);
            notify(t('modules.moduleMembers.updated'));
            setDraftMemberIds(null);
            setSearch('');
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsSaving(false);
        }
    };

    const addSelf = async () => {
        if (isAtLimit) return;

        setIsSaving(true);
        try {
            await munduClient.updateOrganizationModule(organizationId, {
                type: moduleType,
                memberIds: [...savedMemberIds, organizationId],
            });
            await mutate(['organization', organizationId]);
            notify(t('modules.moduleMembers.addedSelf'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <section className="overflow-hidden rounded-2xl border border-starkWhite bg-white shadow-sm">
            <div className="p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="max-w-2xl">
                        <h2 className="font-logo text-xl">
                            {t('modules.moduleMembers.title')}
                        </h2>
                        <p className="mt-1 text-sm leading-6 text-mongooseGray">
                            {description}
                        </p>
                    </div>
                    {canManage && !isEditing && (
                        <Button
                            title={t('modules.moduleMembers.edit')}
                            onClick={startEditing}
                        />
                    )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-lightPeach px-3 py-1 text-xs font-medium">
                        {typeof memberLimit === 'number'
                            ? t('modules.moduleMembers.countWithLimit', {
                                  count: memberIds.length,
                                  limit: memberLimit,
                              })
                            : t('modules.moduleMembers.count', {
                                  count: memberIds.length,
                              })}
                    </span>
                    {!canManage && (
                        <span className="text-xs text-mongooseGray">
                            {t('modules.moduleMembers.adminOnly')}
                        </span>
                    )}
                </div>
            </div>

            {canAddSelf && !isEditing && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-starkWhite bg-lightPeach bg-opacity-30 px-5 py-4 sm:px-6">
                    <div>
                        <p className="text-sm font-medium">
                            {t('modules.moduleMembers.noEditingAccess')}
                        </p>
                        <p className="mt-0.5 text-xs text-mongooseGray">
                            {isAtLimit
                                ? t('modules.moduleMembers.addSelfLimitReached')
                                : t('modules.moduleMembers.addSelfDescription')}
                        </p>
                    </div>
                    <Button
                        title={
                            isSaving
                                ? t('modules.moduleMembers.saving')
                                : t('modules.moduleMembers.addSelf')
                        }
                        onClick={addSelf}
                        disabled={isAtLimit || isSaving}
                        active
                    />
                </div>
            )}

            {employees === undefined ? (
                <p className="border-t border-starkWhite p-5 text-sm text-mongooseGray sm:px-6">
                    {t('common.loading')}
                </p>
            ) : memberIds.length ? (
                <ul className="divide-y divide-starkWhite border-t border-starkWhite">
                    {memberIds.map(memberId => (
                        <MemberRow
                            key={memberId}
                            employee={employeeById.get(memberId)}
                            fallbackId={memberId}
                            action={
                                isEditing
                                    ? {
                                          label: t(
                                              'modules.moduleMembers.remove',
                                          ),
                                          onClick: () => removeMember(memberId),
                                          variant: 'remove',
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </ul>
            ) : (
                <div className="border-t border-starkWhite px-4 py-8 text-center">
                    <p className="text-sm text-mongooseGray">
                        {t('modules.moduleMembers.empty')}
                    </p>
                </div>
            )}

            {isEditing && (
                <div className="border-t border-starkWhite bg-lightPeach bg-opacity-20 p-5 sm:p-6">
                    <h3 className="font-medium">
                        {t('modules.moduleMembers.addTitle')}
                    </h3>

                    <div className="mt-3 overflow-hidden rounded-2xl border border-starkWhite bg-white shadow-sm">
                        <label className="block border-b border-starkWhite px-4 py-2.5">
                            <span className="sr-only">
                                {t('modules.moduleMembers.searchLabel')}
                            </span>
                            <input
                                type="search"
                                value={search}
                                onChange={event =>
                                    setSearch(event.target.value)
                                }
                                placeholder={t(
                                    'modules.moduleMembers.searchPlaceholder',
                                )}
                                className="w-full border-none bg-transparent text-sm outline-none placeholder:text-mongooseGray focus:outline-none"
                            />
                        </label>

                        {availableEmployees.length ? (
                            <ul className="max-h-64 divide-y divide-starkWhite overflow-y-auto">
                                {availableEmployees.map(employee => (
                                    <MemberRow
                                        key={employee.id}
                                        employee={employee}
                                        action={{
                                            label: t(
                                                'modules.moduleMembers.add',
                                            ),
                                            onClick: () =>
                                                addMember(employee.id),
                                            variant: 'add',
                                            disabled: isAtLimit,
                                        }}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="p-4 text-sm text-mongooseGray">
                                {t('modules.moduleMembers.noAvailable')}
                            </p>
                        )}
                    </div>

                    {isAtLimit && (
                        <p className="mt-3 text-sm text-mongooseGray">
                            {t('modules.moduleMembers.limitReached', {
                                limit: memberLimit,
                            })}
                        </p>
                    )}

                    <div className="mt-5 flex justify-end gap-3">
                        <Button
                            title={t('common.cancel')}
                            onClick={cancelEditing}
                            disabled={isSaving}
                        />
                        <Button
                            title={
                                isSaving
                                    ? t('modules.moduleMembers.saving')
                                    : t('common.save')
                            }
                            onClick={saveMembers}
                            disabled={!hasChanges || isSaving}
                            active
                        />
                    </div>
                </div>
            )}
        </section>
    );
};
